import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PrivacyDialog.module.css';

/* ── Sub-components ─────────────────────────────────────────────────── */
function CriterionAccordion({ c }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.criterion}>
      <button
        className={`${styles.cHeader} ${open ? styles.cOpen : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className={styles.cNum}>{t('privacy_dialog.methodology.criterion_label')} {c.id}</span>
        <span className={styles.cNameWrap}>
          <span className={styles.cName}>{c.name}</span>
          <span className={styles.cThreshold}>{c.threshold}</span>
        </span>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}>▼</span>
      </button>
      {open && (
        <div className={styles.cBody}>
          {c.formula && (
            <div className={styles.formula}>
              <span className={styles.formulaLabel}>{t('privacy_dialog.methodology.formula_label')}</span>
              <code>{c.formula}</code>
            </div>
          )}
          <p className={styles.desc}>{c.description}</p>
          {c.thresholds && (
            <div className={styles.thresholds}>
              {c.thresholds.map((th, i) => (
                <div key={i} className={`${styles.thresh} ${styles['thresh_' + th.color]}`}>
                  <span className={styles.threshLabel}>{th.label}</span>
                  <span className={styles.threshVerdict}>{th.verdict}</span>
                </div>
              ))}
            </div>
          )}
          {c.prohibited && (
            <>
              <p className={styles.listTitle}>{t('privacy_dialog.methodology.categorically_prohibited')}</p>
              <ul className={styles.list}>{c.prohibited.map((item, i) => <li key={i}>{item}</li>)}</ul>
            </>
          )}
          {c.borderline && (
            <>
              <p className={styles.listTitle}>{t('privacy_dialog.methodology.borderline')}</p>
              <ul className={`${styles.list} ${styles.listAmber}`}>{c.borderline.map((item, i) => <li key={i}>{item}</li>)}</ul>
            </>
          )}
          <div className={styles.source}>
            <span className={styles.sourceIcon}>📚</span>
            <div>
              <p className={styles.sourceTitle}>{t('privacy_dialog.methodology.primary_sources')}</p>
              <p className={styles.sourceText}>{c.source}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.faqItem}>
      <button className={`${styles.faqQ} ${open ? styles.faqOpen : ''}`} onClick={() => setOpen(o => !o)}>
        <span>{item.q}</span>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}>▼</span>
      </button>
      {open && <p className={styles.faqA}>{item.a}</p>}
    </div>
  );
}

/* ── Main dialog ────────────────────────────────────────────────────── */
export default function PrivacyDialog({ open, onClose }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('Privacy & Data');
  if (!open) return null;

  const TAB_KEYS   = ['Privacy & Data', 'Methodology', 'FAQ'];
  const TAB_LABELS = [
    t('privacy_dialog.tabs.privacy'),
    t('privacy_dialog.tabs.methodology'),
    t('privacy_dialog.tabs.faq'),
  ];

  const CRITERIA = t('privacy_dialog.criteria', { returnObjects: true });
  const FAQS     = t('privacy_dialog.faq',      { returnObjects: true });
  const REFS     = t('privacy_dialog.references', { returnObjects: true });

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        <div className={styles.header}>
          <div className={styles.iconWrap}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <div>
            <h2 className={styles.title}>{t('privacy_dialog.title')}</h2>
            <p className={styles.sub}>{t('privacy_dialog.subtitle')}</p>
          </div>
          <button className={styles.close} onClick={onClose} aria-label={t('privacy_dialog.close_aria')}>✕</button>
        </div>

        {/* Tabs */}
        <div className={styles.tabBar}>
          {TAB_KEYS.map((key, i) => (
            <button
              key={key}
              className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
              onClick={() => setTab(key)}
            >{TAB_LABELS[i]}</button>
          ))}
        </div>

        <div className={styles.body}>

          {/* ── Privacy & Data ── */}
          {tab === 'Privacy & Data' && (
            <>
              <div className={styles.badge}>
                <span className={styles.badgeIcon}>🔒</span>
                <span>{t('privacy_dialog.privacy.no_data_badge')}</span>
              </div>

              <section className={styles.section}>
                <h3>{t('privacy_dialog.privacy.data_sources_title')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('privacy_dialog.privacy.data_sources_text') }} />
              </section>

              <section className={styles.section}>
                <h3>{t('privacy_dialog.privacy.caching_title')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('privacy_dialog.privacy.caching_text') }} />
              </section>

              <section className={styles.section}>
                <h3>{t('privacy_dialog.privacy.search_title')}</h3>
                <p>{t('privacy_dialog.privacy.search_text')}</p>
              </section>

              <section className={styles.section}>
                <h3>{t('privacy_dialog.privacy.analysis_title')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('privacy_dialog.privacy.analysis_text') }} />
              </section>

              <section className={styles.section}>
                <h3>{t('privacy_dialog.privacy.watchlist_title')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('privacy_dialog.privacy.watchlist_text') }} />
              </section>

              <section className={styles.section}>
                <h3>{t('privacy_dialog.privacy.third_party_title')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('privacy_dialog.privacy.third_party_text') }} />
              </section>
            </>
          )}

          {/* ── Methodology ── */}
          {tab === 'Methodology' && (
            <>
              <p className={styles.methodologyIntro}
                dangerouslySetInnerHTML={{ __html: t('privacy_dialog.methodology.intro') }}
              />
              <div className={styles.criteriaList}>
                {Array.isArray(CRITERIA) && CRITERIA.map(c => <CriterionAccordion key={c.id} c={c} />)}
              </div>
              <div className={styles.refBlock} style={{ marginTop: 32 }}>
                <p className={styles.refTitle}>{t('privacy_dialog.methodology.alternative_standards_title')}</p>
                <p dangerouslySetInnerHTML={{ __html: t('privacy_dialog.methodology.alternative_standards_intro') }}
                   style={{ fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.7, marginBottom: 16 }} />

                {/* DJIM */}
                <div className={styles.criterion} style={{ marginBottom: 16 }}>
                  <div className={styles.cHeader} style={{ pointerEvents: 'none' }}>
                    <span className={styles.cNameWrap}>
                      <span className={styles.cName}>{t('privacy_dialog.methodology.djim_name')}</span>
                      <span className={styles.cThreshold}>{t('privacy_dialog.methodology.djim_source_label')}</span>
                    </span>
                  </div>
                  <div className={styles.cBody} style={{ display: 'block' }}>
                    <p className={styles.desc}>{t('privacy_dialog.methodology.djim_desc')}</p>
                    <div className={styles.thresholds}>
                      <div className={`${styles.thresh} ${styles.thresh_green}`}><span className={styles.threshLabel}>{t('privacy_dialog.methodology.thresholds.debt_market_cap')}</span><span className={styles.threshVerdict}>&lt; 33%</span></div>
                      <div className={`${styles.thresh} ${styles.thresh_green}`}><span className={styles.threshLabel}>{t('privacy_dialog.methodology.thresholds.cash_market_cap')}</span><span className={styles.threshVerdict}>&lt; 33%</span></div>
                      <div className={`${styles.thresh} ${styles.thresh_green}`}><span className={styles.threshLabel}>{t('privacy_dialog.methodology.thresholds.receivables_market_cap')}</span><span className={styles.threshVerdict}>&lt; 33%</span></div>
                      <div className={`${styles.thresh} ${styles.thresh_green}`}><span className={styles.threshLabel}>{t('privacy_dialog.methodology.thresholds.haram_revenue')}</span><span className={styles.threshVerdict}>&lt; 5%</span></div>
                    </div>
                    <div className={styles.source}>
                      <span className={styles.sourceIcon}>📚</span>
                      <div>
                        <p className={styles.sourceTitle}>{t('privacy_dialog.methodology.primary_source_label')}</p>
                        <p className={styles.sourceText}>S&amp;P Dow Jones Indices. <em>Dow Jones Islamic Market Indices Methodology.</em> Updated annually. spglobal.com/spdji.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* MSCI */}
                <div className={styles.criterion}>
                  <div className={styles.cHeader} style={{ pointerEvents: 'none' }}>
                    <span className={styles.cNameWrap}>
                      <span className={styles.cName}>{t('privacy_dialog.methodology.msci_name')}</span>
                      <span className={styles.cThreshold}>{t('privacy_dialog.methodology.msci_source_label')}</span>
                    </span>
                  </div>
                  <div className={styles.cBody} style={{ display: 'block' }}>
                    <p className={styles.desc}>{t('privacy_dialog.methodology.msci_desc')}</p>
                    <div className={styles.thresholds}>
                      <div className={`${styles.thresh} ${styles.thresh_green}`}><span className={styles.threshLabel}>{t('privacy_dialog.methodology.thresholds.debt_market_cap')}</span><span className={styles.threshVerdict}>&lt; 33.33%</span></div>
                      <div className={`${styles.thresh} ${styles.thresh_green}`}><span className={styles.threshLabel}>{t('privacy_dialog.methodology.thresholds.cash_market_cap')}</span><span className={styles.threshVerdict}>&lt; 33.33%</span></div>
                      <div className={`${styles.thresh} ${styles.thresh_amber}`}><span className={styles.threshLabel}>{t('privacy_dialog.methodology.thresholds.receivables_market_cap')}</span><span className={styles.threshVerdict}>{t('privacy_dialog.methodology.thresholds.more_lenient')}</span></div>
                      <div className={`${styles.thresh} ${styles.thresh_green}`}><span className={styles.threshLabel}>{t('privacy_dialog.methodology.thresholds.haram_revenue')}</span><span className={styles.threshVerdict}>&lt; 5%</span></div>
                    </div>
                    <div className={styles.source}>
                      <span className={styles.sourceIcon}>📚</span>
                      <div>
                        <p className={styles.sourceTitle}>{t('privacy_dialog.methodology.primary_source_label')}</p>
                        <p className={styles.sourceText}>MSCI. <em>MSCI Islamic Index Series Methodology.</em> Updated annually. msci.com/our-solutions/indexes/islamic-indexes.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.refBlock}>
                <p className={styles.refTitle}>{t('privacy_dialog.methodology.references_title')}</p>
                <ul className={styles.refs}>
                  {Array.isArray(REFS) && REFS.map((ref, i) => (
                    <li key={i} dangerouslySetInnerHTML={{ __html: ref }} />
                  ))}
                </ul>
              </div>
            </>
          )}

          {/* ── FAQ ── */}
          {tab === 'FAQ' && (
            <div className={styles.faqList}>
              {Array.isArray(FAQS) && FAQS.map((item, i) => <FaqItem key={i} item={item} />)}
            </div>
          )}

        </div>

        <div className={styles.footer}>
          <button className={styles.btn} onClick={onClose}>{t('privacy_dialog.close')}</button>
        </div>

      </div>
    </div>
  );
}
